import { Link } from "react-router-dom";
import SiteBreadcrumbs from "@/components/feature/Breadcrumbs";
import { siteInfo } from "@/mocks/site";
import { testimonials } from "@/mocks/testimonials";
import { useSeo } from "@/hooks/useSeo";

export default function About() {
  useSeo({
    title: "关于我们 - 内容原则与网站定位 | 瓜田秘闻社",
    description:
      "了解瓜田秘闻社是谁：一个专注整理娱乐圈公开讨论的内容门户，坚持匿名代称、标注来源脉络、欢迎读者纠错。这里也有我们的联系方式与用户评价。",
    keywords: "黑料吃瓜,关于瓜田秘闻社,圈子八卦,内容原则,娱乐内幕",
    canonical: "/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          name: "关于瓜田秘闻社",
          inLanguage: "zh-CN",
          about: { "@type": "Organization", name: siteInfo.name, email: siteInfo.email },
        },
        {
          "@type": "Organization",
          name: siteInfo.name,
          description: siteInfo.description,
          email: siteInfo.email,
        },
      ],
    },
  });

  return (
    <div className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <SiteBreadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            关于瓜田秘闻社
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            {siteInfo.positioning}。我们相信吃瓜不只是围观，把来龙去脉讲清楚，比单纯的情绪宣泄更有意思。
          </p>
        </div>
      </div>

      <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <div className="h-72 w-full overflow-hidden rounded-lg bg-background-100 md:h-96">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20editorial%20workspace%20with%20open%20notebooks%2C%20a%20vintage%20camera%20and%20soft%20lamp%20light%20on%20a%20cream%20wooden%20desk%2C%20flat%20lay%20still%20life%2C%20coral%20and%20amber%20tones%2C%20no%20people%2C%20high%20detail&width=1200&height=800&seq=gt-about-desk&orientation=landscape"
                alt="暖光下的编辑工作台，笔记本与相机，体现内容整理氛围"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-primary-600">
              <span className="h-px w-6 bg-primary-500"></span> Our Story
            </p>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              从一句“今天吃什么瓜”开始
            </h2>
            <div className="mt-5 space-y-4 text-sm leading-relaxed text-foreground-700">
              <p>
                瓜田秘闻社的起点很简单：几个朋友每次聚会都在聊同一个话题——今天又出了什么瓜？但聊着聊着大家发现，网上的信息往往只有结论，没有前因后果，看完还是懵的。
              </p>
              <p>
                于是我们决定自己动手，把散落各处的讨论整理成有时间线、有背景的短篇。不制造新料，只负责把已有的线索串起来，让人吃得明白。
              </p>
              <p>
                后来内容越做越多，就有了这个站。名字里的“瓜田”，既是吃瓜的田，也提醒我们：瓜是长出来的，得慢慢看，急不得。
              </p>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-6 sm:grid-cols-4">
              {siteInfo.stats.map((stat) => (
                <div key={stat.label}>
                  <p className="font-heading text-2xl font-bold text-primary-600">{stat.value}</p>
                  <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="max-w-2xl">
            <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-primary-600">
              <span className="h-px w-6 bg-primary-500"></span> Principles
            </p>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              四条内容原则
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              这些原则不是写在墙上的口号，而是每一篇稿子发出去之前都会过一遍的检查清单。
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {siteInfo.principles.map((principle, index) => (
              <div
                key={principle}
                className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 font-heading text-lg font-bold text-accent-700">
                  {index + 1}
                </span>
                <p className="text-sm leading-relaxed text-foreground-700">{principle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              读者怎么说
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              我们没什么了不起的成绩，能被读者当成每天固定打开的一个站点，就是最好的评价。
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="text-sm text-foreground-600">联系邮箱</p>
                <p className="mt-1 font-medium text-foreground-900">{siteInfo.email}</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="text-sm text-foreground-600">内容合作与纠错</p>
                <p className="mt-1 font-medium text-foreground-900">{siteInfo.editorEmail}</p>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-mail-send-line"></i> 给我们留言
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-7">
            {testimonials.map((item) => (
              <figure key={item.id} className="rounded-lg bg-background-100 p-5">
                <i className="ri-double-quotes-l text-xl text-primary-300"></i>
                <blockquote className="mt-2 text-sm leading-relaxed text-foreground-700">
                  {item.quote}
                </blockquote>
                <figcaption className="mt-3 text-xs text-foreground-500">
                  {item.name} · {item.role}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}